import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getSubscription,
  getCredits,
  getSubscriptionPlans,
  getBillingMe,
  createCheckoutSession,
  createPortalSession,
  Subscription,
  SubscriptionPlan,
  UserCredits,
  BillingMe,
} from '../../services/api';

interface BillingState {
  subscription: Subscription | null;
  credits: UserCredits | null;
  plans: SubscriptionPlan[];
  loading: boolean;
  error: string | null;
  tier: string;
  isPaid: boolean;
  isTrialing: boolean;
  isComped: boolean;
  /** ISO trial end from GET /api/billing/me (auto-trial anchor). */
  trialEndsAt: string | null;
  /** True if the user should receive Pro features (paid, trial, or comp). SSOT for client-side gating. */
  hasProAccess: boolean;
  /** True if the 14-day auto-trial has lapsed (backend is_expired). */
  isExpired: boolean;
  /** True when the user must be blocked by the paywall (expired + no Pro access). */
  isLocked: boolean;
}

const initialState: BillingState = {
  subscription: null,
  credits: null,
  plans: [],
  loading: false,
  error: null,
  tier: 'free',
  isPaid: false,
  isTrialing: false,
  isComped: false,
  trialEndsAt: null,
  hasProAccess: false,
  isExpired: false,
  isLocked: false,
};

/**
 * Derive client-side gating flags from the backend entitlement (GET /api/billing/me).
 * BillingMe has no `is_paid` field - Pro access is derived from entitlement_tier
 * ('pro'|'admin_grant') plus the trial/comp overlays, mirroring
 * EntitlementService.has_pro_access on the backend (the SSOT).
 */
function deriveFromMe(me: BillingMe) {
  const tier = me.entitlement_tier || 'free';
  const isComped = !!me.is_comped;
  const isTrialing = !!me.is_trialing;
  const isPaid =
    !isComped &&
    !isTrialing &&
    tier !== 'free' &&
    (me.status === 'active' || me.status === 'canceled');
  // pending_verification keeps tier='free' on the backend, so it correctly yields no access.
  const hasProAccess = tier !== 'free' || isComped || isTrialing;
  const isExpired = !!me.is_expired;
  const isLocked = isExpired && !hasProAccess;
  return { tier, isComped, isTrialing, isPaid, hasProAccess, isExpired, isLocked };
}

export const fetchSubscription = createAsyncThunk(
  'billing/fetchSubscription',
  async () => {
    const response = await getSubscription();
    return response;
  }
);

export const fetchCredits = createAsyncThunk(
  'billing/fetchCredits',
  async () => {
    const response = await getCredits();
    return response;
  }
);

export const fetchPlans = createAsyncThunk(
  'billing/fetchPlans',
  async () => {
    const response = await getSubscriptionPlans();
    return response.plans;
  }
);

/**
 * Resolve the authenticated user's entitlement from the backend SSOT.
 * Returns null on 401 (anonymous/public) or any error so callers fall back
 * to Free defaults instead of stale state. Dispatch globally on auth-ready
 * (see App.tsx) so paid/comp gating is populated before any AI button renders.
 */
function isBillingMeUnauthorized(err: unknown): boolean {
  const status =
    (err as { response?: { status?: number } })?.response?.status ??
    (err as { status?: number })?.status;
  return status === 401;
}

export const fetchBillingMe = createAsyncThunk(
  'billing/fetchMe',
  async () => {
    try {
      return await getBillingMe();
    } catch (err) {
      // Only anon/expired sessions → Free defaults. Network/5xx must not
      // wipe a trialing user's state (badge vanished until full refresh).
      if (isBillingMeUnauthorized(err)) {
        return null;
      }
      throw err;
    }
  }
);

export const fetchBillingData = createAsyncThunk(
  'billing/fetchAll',
  async () => {
    const [subscription, credits, plans] = await Promise.all([
      getSubscription().catch(() => ({ subscription: null, plan: 'free', status: 'inactive', freeScansRemaining: 10 })),
      getCredits().catch(() => ({ balance: 0, dailyFreeRemaining: 1, total: 0 })),
      getSubscriptionPlans().catch(() => ({ plans: [] })),
    ]);

    return {
      subscription: subscription.subscription,
      credits,
      plans: plans.plans || [],
    };
  }
);

export const openCheckout = createAsyncThunk(
  'billing/openCheckout',
  async (planId?: string) => {
    const response = await createCheckoutSession(planId);
    if (response.url) {
      window.location.href = response.url;
    }
    return response;
  }
);

export const openPortal = createAsyncThunk(
  'billing/openPortal',
  async () => {
    const response = await createPortalSession();
    if (response.url) {
      window.location.href = response.url;
    }
    return response;
  }
);

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    /** Clear entitlement on logout so the next session never inherits stale trial state. */
    resetBillingEntitlement: () => initialState,
    setTier: (state, action: PayloadAction<{ tier: string; isPaid: boolean; isTrialing: boolean }>) => {
      state.tier = action.payload.tier;
      state.isPaid = action.payload.isPaid;
      state.isTrialing = action.payload.isTrialing;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchBillingMe - canonical entitlement source (GET /api/billing/me)
      .addCase(fetchBillingMe.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBillingMe.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const d = deriveFromMe(action.payload);
          state.tier = d.tier;
          state.isComped = d.isComped;
          state.isTrialing = d.isTrialing;
          state.isPaid = d.isPaid;
          state.trialEndsAt = action.payload.trial_ends_at ?? null;
          state.hasProAccess = d.hasProAccess;
          state.isExpired = d.isExpired;
          state.isLocked = d.isLocked;
        } else {
          // 401/anonymous/error → Free defaults (no stale Pro access).
          state.tier = 'free';
          state.isComped = false;
          state.isTrialing = false;
          state.isPaid = false;
          state.trialEndsAt = null;
          state.hasProAccess = false;
          state.isExpired = false;
          state.isLocked = false;
        }
      })
      .addCase(fetchBillingMe.rejected, (state) => {
        // Transient failure — keep the last known entitlement snapshot.
        state.loading = false;
      })
      // fetchBillingData
      .addCase(fetchBillingData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBillingData.fulfilled, (state, action) => {
        state.loading = false;
        state.subscription = action.payload.subscription;
        state.credits = action.payload.credits;
        state.plans = action.payload.plans;
        // Entitlement (tier/trial/paid/access) is owned by fetchBillingMe only.
        // Legacy /subscription has planId=free + trialEnd=null for auto-trial.
      })
      .addCase(fetchBillingData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load billing data';
      })
      // fetchSubscription
      .addCase(fetchSubscription.fulfilled, (state, action) => {
        state.subscription = action.payload.subscription;
        // Entitlement fields unchanged — fetchBillingMe is the SSOT.
      })
      // fetchCredits
      .addCase(fetchCredits.fulfilled, (state, action) => {
        state.credits = action.payload;
      })
      // fetchPlans
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.plans = action.payload;
      })
      // openCheckout
      .addCase(openCheckout.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to open checkout';
      })
      // openPortal
      .addCase(openPortal.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to open billing portal';
      });
  },
});

export const { clearError, resetBillingEntitlement, setTier } = billingSlice.actions;
export default billingSlice.reducer;
